import {
  AVATAR_COLORS,
  BadgeId,
  BLOCK_THEMES,
  isUnlocked,
  PROFILE_ICONS,
  type BadgeId as BadgeIdType,
  type Equipped,
} from '@pebblecode/shared';
import { useState } from 'react';

import { api } from '../api/client.js';
import { onLinkClick, useTitle } from '../app/router.js';
import { AccountMenu } from '../components/AccountMenu.js';
import { Brand, LangSwitch, ThemeToggle } from '../components/TopBar.js';
import { useT, type Messages } from '../i18n/index.js';
import { BadgeMedal, ProfileIcon } from '../rewards/RewardIcons.js';
import { LevelBar } from '../rewards/RewardsUi.js';
import { eventLabel } from '../rewards/rewardText.js';
import { useExerciseTitles } from '../state/catalog.js';
import { useUiLang } from '../state/prefs.js';
import { refreshRewards, useRewards } from '../state/rewards.js';

function lockLabel(t: Messages, item: { level: number; badge?: BadgeIdType | null }): string {
  return item.badge
    ? t.rewards.lockedBadge(t.rewards.badge[item.badge]?.[0] ?? item.badge)
    : t.rewards.lockedLevel(item.level);
}

/** `/me` — private progress: level, streak, badges, cosmetics, recent points. */
export function RewardsPage() {
  const t = useT();
  const lang = useUiLang();
  const s = useRewards();
  const titles = useExerciseTitles(lang);
  const [busy, setBusy] = useState(false);
  useTitle(t.rewards.title);

  if (!s) {
    return <main className="center-page">{t.common.loading}</main>;
  }
  const earned = new Map(s.badges.map((b) => [b.id, b.at]));
  const badgeSet = new Set(earned.keys());
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' });

  const equip = async (patch: Partial<Equipped>) => {
    setBusy(true);
    try {
      await api.equip(patch);
      await refreshRewards();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="home">
      <header className="topbar home-topbar">
        <Brand />
        <div className="topbar-spacer" />
        <nav className="home-nav">
          <a href="/" onClick={onLinkClick}>
            {t.nav.exercises}
          </a>
          <a href="/me" onClick={onLinkClick} aria-current="page">
            {t.nav.progress}
          </a>
        </nav>
        <AccountMenu />
        <LangSwitch />
        <ThemeToggle />
      </header>

      <main className="home-main rewards-main">
        <div className="home-left">
          <section className="card rewards-hero">
            <span
              className="rewards-avatar"
              style={{
                background: AVATAR_COLORS.find((c) => c.id === s.equipped.avatarColor)?.color,
              }}
            >
              <ProfileIcon id={s.equipped.icon} size={30} />
            </span>
            <div className="rewards-hero-text">
              <span className="eyebrow">{t.rewards.title}</span>
              <h1>{t.rewards.level(s.level)}</h1>
              <strong className="rewards-points">{t.rewards.points(s.points)}</strong>
              <LevelBar s={s} />
            </div>
            <div className="streak-box">
              <span className="eyebrow">{t.rewards.streak}</span>
              <strong>🔥 {t.rewards.streakDays(s.streak.current)}</strong>
              <span>{t.rewards.bestStreak(s.streak.best)}</span>
              <span className={s.streak.today ? 'tone-ok' : 'muted-text'}>
                {s.streak.today ? t.rewards.todayDone : t.rewards.todayOpen}
              </span>
            </div>
          </section>

          <section className="catalog" aria-labelledby="badges-title">
            <div className="catalog-head">
              <h2 id="badges-title">{t.rewards.badges}</h2>
              <span className="muted-text">
                {s.badges.length} / {BadgeId.options.length}
              </span>
            </div>
            <div className="badge-grid">
              {BadgeId.options.map((id) => {
                const at = earned.get(id);
                const [name, how] = t.rewards.badge[id] ?? [id, ''];
                return (
                  <div key={id} className={`card badge-card${at ? '' : ' locked'}`}>
                    <BadgeMedal id={id} earned={!!at} />
                    <div>
                      <strong>{name}</strong>
                      <span>{at ? t.rewards.earned(fmt(at)) : how}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="catalog" aria-labelledby="unlock-title">
            <div className="catalog-head">
              <h2 id="unlock-title">{t.rewards.unlockables}</h2>
              <span className="muted-text">{t.rewards.unlockablesText}</span>
            </div>

            <div className="card unlock-card">
              <h3>{t.rewards.avatarColor}</h3>
              <p className="muted-text">{t.rewards.avatarColorText}</p>
              <div className="unlock-row">
                {AVATAR_COLORS.map((c) => {
                  const open = isUnlocked(c, s.level, badgeSet);
                  const on = s.equipped.avatarColor === c.id;
                  return (
                    <button
                      key={c.id}
                      className={`swatch${on ? ' on' : ''}`}
                      disabled={!open || busy}
                      onClick={() => void equip({ avatarColor: c.id })}
                      aria-pressed={on}
                      title={open ? t.rewards.colors[c.id] : lockLabel(t, c)}
                      aria-label={`${t.rewards.colors[c.id]}${open ? '' : ` — ${lockLabel(t, c)}`}`}
                    >
                      <span style={{ background: c.color }} />
                      {!open && <small>{c.level}</small>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="card unlock-card">
              <h3>{t.rewards.icon}</h3>
              <div className="unlock-row">
                {PROFILE_ICONS.map((i) => {
                  const open = isUnlocked(i, s.level, badgeSet);
                  const on = s.equipped.icon === i.id;
                  return (
                    <button
                      key={i.id}
                      className={`icon-choice${on ? ' on' : ''}`}
                      disabled={!open || busy}
                      onClick={() => void equip({ icon: i.id })}
                      aria-pressed={on}
                      title={open ? t.rewards.icons[i.id] : lockLabel(t, i)}
                    >
                      <ProfileIcon id={i.id} size={22} />
                      <span>{open ? t.rewards.icons[i.id] : lockLabel(t, i)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="card unlock-card">
              <h3>{t.rewards.blockTheme}</h3>
              <div className="theme-row">
                {BLOCK_THEMES.map((th) => {
                  const open = isUnlocked(th, s.level, badgeSet);
                  const on = s.equipped.blockTheme === th.id;
                  return (
                    <button
                      key={th.id}
                      className={`theme-choice${on ? ' on' : ''}`}
                      disabled={!open || busy}
                      onClick={() => void equip({ blockTheme: th.id })}
                      aria-pressed={on}
                    >
                      <span className="theme-swatches">
                        {th.colors.map((c) => (
                          <i key={c} style={{ background: c }} />
                        ))}
                      </span>
                      <strong>{t.rewards.themes[th.id]}</strong>
                      <span>
                        {on ? t.rewards.equipped : open ? t.rewards.equip : lockLabel(t, th)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        <aside className="home-right">
          <section className="card side-card">
            <h2>{t.rewards.howToEarn}</h2>
            <ul className="rules">
              <li>{t.rewards.rules.solve}</li>
              <li>{t.rewards.rules.noHint}</li>
              <li>{t.rewards.rules.daily}</li>
              <li>{t.rewards.rules.learn}</li>
            </ul>
            <p>{t.rewards.rules.private}</p>
          </section>
          <section className="card side-card">
            <h2>{t.rewards.recent}</h2>
            {s.recent.length === 0 ? (
              <p>{t.rewards.noRecent}</p>
            ) : (
              <ul className="recent-list">
                {s.recent.map((e) => (
                  <li key={e.id}>
                    <span className="recent-points">+{e.points}</span>
                    <span className="recent-what">{eventLabel(e, t, titles)}</span>
                    <span className="recent-when">{fmt(e.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </main>
    </div>
  );
}
